import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Modal, PrimaryButton, FieldSelect } from '../../components';
import { Form as FinalForm, Field } from 'react-final-form';
import ExpandingTextarea from '../../components/ExpandingTextarea/ExpandingTextarea';
import { required } from '../../util/validators';
import classNames from 'classnames';

import css from './ReportMessageModal.module.css';

const REPORT_REASONS = ['spam', 'harassment', 'offensive', 'scam', 'other'];

const ReportMessageModalComponent = props => {
    const {
        isOpen,
        onCloseModal,
        onManageDisableScrolling,
        onSubmitReport,
        inProgress,
        error,
        success,
        intl,
    } = props;

    const [formSubmitted, setFormSubmitted] = useState(false);

    const handleSubmit = values => {
        setFormSubmitted(true);
        onSubmitReport(values);
    };

    const reasonRequiredMessage = intl.formatMessage({
        id: 'ReportMessageModal.reasonRequired',
        defaultMessage: 'Please select a reason.',
    });

    const otherDetailsRequiredMessage = intl.formatMessage({
        id: 'ReportMessageModal.otherDetailsRequired',
        defaultMessage: 'Please provide details for the "other" reason.',
    });

    return (
        <Modal
            id="ReportMessageModal"
            isOpen={isOpen}
            onClose={onCloseModal}
            onManageDisableScrolling={onManageDisableScrolling}
            containerClassName={css.modalContainer}
            contentClassName={css.modalContent}
        >
                <h2 className={css.modalTitle}>
                    <FormattedMessage id="ReportMessageModal.title" defaultMessage="Report this message" />
                </h2>

                {success ? (
                    <div className={css.successMessage}>
                        <FormattedMessage
                            id="ReportMessageModal.successMessage"
                            defaultMessage="Thanks, we've received your report."
                        />
                        <div className={css.successButtonContainer}>
                            <PrimaryButton onClick={onCloseModal}>
                                <FormattedMessage id="ReportMessageModal.closeButton" defaultMessage="Close" />
                            </PrimaryButton>
                        </div>
                    </div>
                ) : (
                    <FinalForm
                        onSubmit={handleSubmit}
                        render={fieldRenderProps => {
                            const { handleSubmit, invalid, values } = fieldRenderProps;
                            const isOtherSelected = values.reason === 'other';

                            const submitDisabled = invalid || inProgress;

                            return (
                                <form onSubmit={handleSubmit}>
                                    <div className={css.formRow}>
                                        <FieldSelect
                                            id="reason"
                                            name="reason"
                                            label={intl.formatMessage({
                                                id: 'ReportMessageModal.reasonLabel',
                                                defaultMessage: 'Reason for reporting',
                                            })}
                                            validate={required(reasonRequiredMessage)}
                                        >
                                            <option disabled value="">
                                                {intl.formatMessage({
                                                    id: 'ReportMessageModal.reasonPlaceholder',
                                                    defaultMessage: 'Select a reason',
                                                })}
                                            </option>
                                            {REPORT_REASONS.map(reason => (
                                                <option key={reason} value={reason}>
                                                    {intl.formatMessage({
                                                        id: `ReportMessageModal.reason.${reason}`,
                                                        defaultMessage: reason.charAt(0).toUpperCase() + reason.slice(1),
                                                    })}
                                                </option>
                                            ))}
                                        </FieldSelect>
                                    </div>

                                    <div className={css.formRow}>
                                        <Field
                                            name="otherDetails"
                                            validate={isOtherSelected ? required(otherDetailsRequiredMessage) : undefined}
                                            render={({ input, meta }) => (
                                                <div className={css.expandingTextareaWrapper}>
                                                    <label className={css.expandingTextareaLabel}>
                                                        {isOtherSelected ? (
                                                            <FormattedMessage
                                                                id="ReportMessageModal.otherDetailsLabelRequired"
                                                                defaultMessage="Please provide more details *"
                                                            />
                                                        ) : (
                                                            <FormattedMessage
                                                                id="ReportMessageModal.otherDetailsLabelOptional"
                                                                defaultMessage="Additional details (optional)"
                                                            />
                                                        )}
                                                    </label>
                                                    <ExpandingTextarea
                                                        {...input}
                                                        className={classNames(css.expandingTextarea, {
                                                            [css.errorInput]: meta.error && meta.touched,
                                                        })}
                                                        minRows={3}
                                                    />
                                                    {meta.error && meta.touched && (
                                                        <div className={css.errorMessage}>{meta.error}</div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    {error && (
                                        <div className={css.errorContainer}>
                                            <p className={css.errorMessage}>
                                                {error.status === 409 ? (
                                                    <FormattedMessage
                                                        id="ReportMessageModal.alreadyReportedError"
                                                        defaultMessage="You've already reported this message."
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        id="ReportMessageModal.genericError"
                                                        defaultMessage="Something went wrong. Please try again or contact support."
                                                    />
                                                )}
                                            </p>
                                        </div>
                                    )}

                                    <div className={css.submitContainer}>
                                        <PrimaryButton type="submit" inProgress={inProgress} disabled={submitDisabled}>
                                            <FormattedMessage id="ReportMessageModal.submit" defaultMessage="Submit report" />
                                        </PrimaryButton>
                                    </div>
                                </form>
                            );
                        }}
                    />
                )}
        </Modal>
    );
};

export default injectIntl(ReportMessageModalComponent);
